/**
 * Locally saved email drafts ("Save Draft" in ComposeEmailDialog).
 *
 * Drafts live in localStorage under one key per workspace, newest first and
 * capped. Each draft carries a `contextKey` (a meeting follow-up, a contact,
 * a fixed recipient set) so reopening the composer in the same context
 * restores it. There is no backend for drafts — every access is wrapped in
 * try/catch because storage can be blocked, full, or hold corrupt JSON.
 */

/** Structural copy of ComposeEmailDialog's Recipient (kept here so the util stays dependency-free). */
export interface DraftRecipient {
  id: string;
  name: string;
  email: string;
  title?: string;
  company?: string;
  phone?: string;
  location?: string;
}

export interface SavedEmailDraft {
  id: string;
  savedAt: string;
  /** Context the draft belongs to; drafts without one are never restored. */
  contextKey?: string;
  /** Recipient emails (kept for readability of the stored record). */
  to: string[];
  cc: string[];
  bcc: string[];
  toRecipients?: DraftRecipient[];
  ccRecipients?: DraftRecipient[];
  bccRecipients?: DraftRecipient[];
  /** One shared email (true) vs individual emails (false). */
  groupSend?: boolean;
  subject: string;
  body: string;
}

export interface EmailDraftInput {
  contextKey?: string | null;
  toRecipients: DraftRecipient[];
  ccRecipients: DraftRecipient[];
  bccRecipients: DraftRecipient[];
  groupSend: boolean;
  subject: string;
  body: string;
}

export const EMAIL_DRAFTS_LIMIT = 50;

export function emailDraftsStorageKey(workspaceId?: string | null): string {
  return `coffee:email-drafts:${workspaceId ?? "preview"}`;
}

export function readEmailDrafts(workspaceId?: string | null): SavedEmailDraft[] {
  try {
    const raw = localStorage.getItem(emailDraftsStorageKey(workspaceId));
    const parsed = raw ? JSON.parse(raw) : [];
    return Array.isArray(parsed) ? parsed.filter((d) => d && typeof d === "object") : [];
  } catch {
    return [];
  }
}

function writeEmailDrafts(workspaceId: string | null | undefined, drafts: SavedEmailDraft[]): boolean {
  try {
    localStorage.setItem(emailDraftsStorageKey(workspaceId), JSON.stringify(drafts.slice(0, EMAIL_DRAFTS_LIMIT)));
    return true;
  } catch {
    return false;
  }
}

/** Most recent draft saved for `contextKey`, or null. */
export function getEmailDraft(workspaceId: string | null | undefined, contextKey: string | null | undefined): SavedEmailDraft | null {
  if (!contextKey) return null;
  return readEmailDrafts(workspaceId).find((d) => d.contextKey === contextKey) ?? null;
}

/**
 * Save a draft as the newest entry. A draft with a context key replaces any
 * earlier draft for the same context (one draft per context). Returns false
 * when storage is unavailable.
 */
export function saveEmailDraft(
  workspaceId: string | null | undefined,
  input: EmailDraftInput,
  now: Date = new Date(),
): boolean {
  const contextKey = input.contextKey || undefined;
  const others = readEmailDrafts(workspaceId).filter((d) => !contextKey || d.contextKey !== contextKey);
  const draft: SavedEmailDraft = {
    id: `draft-${now.getTime()}`,
    savedAt: now.toISOString(),
    ...(contextKey ? { contextKey } : {}),
    to: input.toRecipients.map((r) => r.email),
    cc: input.ccRecipients.map((r) => r.email),
    bcc: input.bccRecipients.map((r) => r.email),
    toRecipients: input.toRecipients,
    ccRecipients: input.ccRecipients,
    bccRecipients: input.bccRecipients,
    groupSend: input.groupSend,
    subject: input.subject,
    body: input.body,
  };
  return writeEmailDrafts(workspaceId, [draft, ...others]);
}

/**
 * Update the body of an existing draft in place (e.g. the meeting Email tab
 * editor, so an older saved draft never overrides newer in-tab edits).
 * No-op returning false when no draft exists for the context.
 */
export function updateEmailDraftBody(
  workspaceId: string | null | undefined,
  contextKey: string | null | undefined,
  body: string,
): boolean {
  if (!contextKey) return false;
  const drafts = readEmailDrafts(workspaceId);
  const idx = drafts.findIndex((d) => d.contextKey === contextKey);
  if (idx === -1) return false;
  if (drafts[idx].body === body) return true;
  drafts[idx] = { ...drafts[idx], body };
  return writeEmailDrafts(workspaceId, drafts);
}

/** Remove every draft saved for `contextKey` (after a successful send). */
export function clearEmailDraft(workspaceId: string | null | undefined, contextKey: string | null | undefined): void {
  if (!contextKey) return;
  const drafts = readEmailDrafts(workspaceId);
  const remaining = drafts.filter((d) => d.contextKey !== contextKey);
  if (remaining.length !== drafts.length) writeEmailDrafts(workspaceId, remaining);
}

/**
 * Default context for a composer opened on a fixed set of recipients (a
 * contact's sheet, the grid context menu, a bulk selection): the sorted
 * recipient ids. Null when there are no recipients — no stable context, so
 * nothing is restored.
 */
export function recipientsDraftKey(recipients: DraftRecipient[] | null | undefined): string | null {
  const ids = (recipients ?? []).map((r) => r.id || r.email).filter(Boolean);
  if (ids.length === 0) return null;
  return `recipients:${[...new Set(ids)].sort().join(",")}`;
}

/** Context key for a meeting's follow-up email. */
export function meetingFollowUpDraftKey(noteId: string | null | undefined): string | null {
  return noteId ? `meeting-followup:${noteId}` : null;
}
